import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/stores/auth";

export const wishlistKeys = {
  ids: ["wishlist", "ids"] as const,
  lists: ["wishlist", "lists"] as const,
  items: (id: string) => ["wishlist", "items", id] as const,
  shared: (token: string) => ["wishlist", "shared", token] as const,
};

/** Product ids the signed-in user has saved anywhere (drives every heart). */
export function useSavedIds() {
  const signedIn = useAuth((s) => s.status === "authenticated");
  return useQuery({
    queryKey: wishlistKeys.ids,
    queryFn: async () => new Set((await unwrap(api.GET("/api/v1/wishlists/saved-ids/"))).product_ids),
    enabled: signedIn,
    staleTime: 60_000,
  });
}

export function useToggleWishlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }: { slug: string; productId: string }) =>
      unwrap(api.POST("/api/v1/wishlists/toggle/", { body: { product: slug } })),
    onMutate: async ({ productId }) => {
      await qc.cancelQueries({ queryKey: wishlistKeys.ids });
      const previous = qc.getQueryData<Set<string>>(wishlistKeys.ids);
      const next = new Set(previous ?? []);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      qc.setQueryData(wishlistKeys.ids, next);
      return { previous };
    },
    onError: (_e, _v, ctx) => ctx?.previous && qc.setQueryData(wishlistKeys.ids, ctx.previous),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: wishlistKeys.ids });
      void qc.invalidateQueries({ queryKey: wishlistKeys.lists });
      void qc.invalidateQueries({ queryKey: ["wishlist", "items"] });
    },
  });
}

export function useWishlists(enabled = true) {
  return useQuery({ queryKey: wishlistKeys.lists, queryFn: () => unwrap(api.GET("/api/v1/wishlists/")), enabled });
}

export function useWishlistItems(id: string | undefined) {
  return useQuery({
    queryKey: wishlistKeys.items(id ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/wishlists/{id}/items/", { params: { path: { id: id! } } })),
    enabled: !!id,
  });
}

function useListMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["wishlist"] });
    },
  });
}

export const useCreateWishlist = () =>
  useListMutation((body: { name: string; is_public?: boolean }) =>
    unwrap(api.POST("/api/v1/wishlists/", { body: { name: body.name, is_public: body.is_public ?? false } })),
  );
export const useUpdateWishlist = () =>
  useListMutation(({ id, ...body }: { id: string; name?: string; is_public?: boolean }) =>
    unwrap(api.PATCH("/api/v1/wishlists/{id}/", { params: { path: { id } }, body })),
  );
export const useDeleteWishlist = () =>
  useListMutation((id: string) => unwrap(api.DELETE("/api/v1/wishlists/{id}/", { params: { path: { id } } })));
export const useAddToWishlist = () =>
  useListMutation(({ id, slug }: { id: string; slug: string }) =>
    unwrap(api.POST("/api/v1/wishlists/{id}/items/", { params: { path: { id } }, body: { product: slug } })),
  );
export const useRemoveFromWishlist = () =>
  useListMutation(({ id, productId }: { id: string; productId: string }) =>
    unwrap(api.DELETE("/api/v1/wishlists/{id}/items/{product_id}/", { params: { path: { id, product_id: productId } } })),
  );

export function useSharedWishlist(token: string | undefined) {
  return useQuery({
    queryKey: wishlistKeys.shared(token ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/wishlists/shared/{token}/", { params: { path: { token: token! } } })),
    enabled: !!token,
    retry: false,
  });
}
